import { Box, capitalize, Stack, Typography } from '@mui/material';
import type { ApiCredentialsProperties, User } from '@repo/api-gw-sdk';
import { forwardRef } from 'react';

import { useUser } from '@/contexts/useUser';

export const getUserName = (
  user?: User | ApiCredentialsProperties,
  ignoreDomain?: boolean
) => {
  let name = '';
  if (user) {
    if ('givenName' in user && user.givenName) {
      name = capitalize(user.givenName);
    }

    if (!name && 'name' in user && user.name) {
      name = capitalize(user.name);
    }

    if (!name && 'email' in user && user.email) {
      name = user.email;
    }
  }

  return ignoreDomain ? name?.split('@')[0] : name;
};

export const UserCircle = forwardRef(
  (
    {
      user,
      ignoreDomain,
      text,
      ...rest
    }: {
      user?: User | ApiCredentialsProperties;
      ignoreDomain?: boolean;
      text?: string;
    },
    ref
  ) => {
    const name = text || getUserName(user, ignoreDomain);
    return (
      <Box
        {...rest}
        ref={ref}
        className='min-w-[24px] min-h-[24px] w-[24px] h-[24px] flex items-center justify-center rounded-full bg-(--mui-palette-success-main)'
      >
        <Typography className='font-semibold text-white capitalize'>
          {(name || '')[0]}
        </Typography>
      </Box>
    );
  }
);

export const UserBadge = ({
  user,
  className,
}: {
  user: User;
  className?: string;
}) => {
  const { user: currentUser } = useUser();
  const name = (user.givenName || user.email || '').split('@')[0];

  const isSamlUser = user.userOrigin && user.userOrigin !== 'LOCAL_USER';
  const nameSuffix =
    currentUser?.id === user.id ? ' (you)' : isSamlUser ? ' (SAML)' : '';

  return (
    <Stack direction='row' alignItems='center' className={className}>
      <UserCircle user={user} ignoreDomain={true} />
      <Stack className='ml-[12px]'>
        <Typography variant='subtitle1'>{name + nameSuffix}</Typography>
        <Typography variant='body1'>{user.email}</Typography>
      </Stack>
    </Stack>
  );
};

export const EntityBadge = ({
  title,
  subtitle,
  className,
}: {
  title: string;
  subtitle: string;
  className?: string;
}) => {
  return (
    <Stack direction='row' alignItems='center' className={className}>
      <UserCircle text={title || subtitle} />
      <Stack className='ml-[12px]'>
        <Typography variant='subtitle1'>{title}</Typography>
        <Typography variant='body1'>{subtitle}</Typography>
      </Stack>
    </Stack>
  );
};
